import { useEffect, useState } from "react";
import { API, BACKEND } from "@/lib/api";


export const useAppSocket = (appId) => {
    const [events, setEvents] = useState([]);
    const [metrics, setMetrics] = useState(null);
    const [uptime, setUptime] = useState([]);
    const [hooks, setHooks] = useState([]);
    const [nodestats, setNodestats] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [agents, setAgents] = useState([]);
    const [cronjobs, setCronjobs] = useState([]);
    const [sslcheck, setSslcheck] = useState(null);
    const [live, setLive] = useState(false);
    const [deliveryClearStamp, setDeliveryClearStamp] = useState(0);

    useEffect(() => {
        if (!appId) return undefined;
        setEvents([]);
        setMetrics(null);
        setUptime([]);
        setHooks([]);
        setNodestats(null);
        setDeliveries([]);
        setAgents([]);
        setCronjobs([]);
        setSslcheck(null);
        let dead = false;
        let ws;
        let retry;

        const load = async () => {
            try {
                const r = await fetch(`${API}/apps/${appId}/events?limit=40`, {
                    credentials: "include",
                });
                if (r.ok) {
                    const d = await r.json();
                    if (!dead) setEvents(d.events || []);
                }
            } catch {
                /* initial history is best-effort */
            }
        };

        const connect = () => {
            ws = new WebSocket(`${API.replace(/^http/, "ws")}/ws/apps/${appId}`);
            ws.onopen = () => setLive(true);
            ws.onmessage = (m) => {
                try {
                    const msg = JSON.parse(m.data);
                    if (msg.type === "stream_cleared") setEvents([]);
                    if (msg.type === "deliveries_cleared") {
                        setDeliveries([]);
                        setDeliveryClearStamp((s) => s + 1);
                    }
                    if (msg.type === "event")
                        setEvents((prev) =>
                            prev.some((e) => e.id === msg.event.id)
                                ? prev
                                : [msg.event, ...prev].slice(0, 60),
                        );
                    if (msg.type === "metrics") setMetrics(msg);
                    if (msg.type === "agents") setAgents(msg.hosts || []);
                    if (msg.type === "cronjobs") setCronjobs(msg.jobs || []);
                    if (msg.type === "sslcheck") setSslcheck(msg.check || null);
                    if (msg.type === "uptime")
                        setUptime((prev) =>
                            prev.some((c) => c.id === msg.check.id)
                                ? prev
                                : [msg.check, ...prev].slice(0, 120),
                        );
                    if (msg.type === "hook")
                        setHooks((prev) =>
                            [msg.hook, ...prev.filter((h) => h.id !== msg.hook.id)].slice(0, 60),
                        );
                    if (msg.type === "nodestats") setNodestats(msg.snapshot);
                    if (msg.type === "delivery")
                        setDeliveries((prev) =>
                            [msg.delivery, ...prev.filter((d) => d.id !== msg.delivery.id)].slice(
                                0,
                                60,
                            ),
                        );
                } catch {
                    /* ignore malformed frames */
                }
            };
            ws.onclose = () => {
                setLive(false);
                if (!dead) retry = setTimeout(connect, 3000);
            };
            ws.onerror = () => ws.close();
        };

        load();
        connect();
        return () => {
            dead = true;
            clearTimeout(retry);
            ws?.close();
        };
    }, [appId]);

    return { events, metrics, uptime, hooks, nodestats, deliveries, agents, cronjobs, sslcheck, live, deliveryClearStamp };
};
